import {
  Card,
  CardContent,
} from "js/components/ui/card";
import { StatusBadge } from "js/components/ui/status-badge";
import { Button } from "js/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "js/components/ui/dropdown-menu";
import { cn } from "lib/utils";
import {
  CalendarIcon,
  HomeIcon,
  MoreHorizontalIcon,
  ShareIcon,
  UserIcon,
} from "lucide-react";
import { useIsMobile } from "hooks/useMediaQuery";
import { formatCurrency } from "utils/utils";

// ---------------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------------

type ReportStatus = string;

const STATUS_MAP: Record<
  string,
  { label: string; variant: "success" | "warning" | "info" | "pending" | "neutral" | "error" }
> = {
  payment_in_progress: { label: "Pago Pendiente", variant: "pending" },
  "Payment in Progress": { label: "Pago Pendiente", variant: "pending" },
  completed: { label: "Completado", variant: "success" },
  Completed: { label: "Completado", variant: "success" },
  in_progress: { label: "En progreso", variant: "warning" },
  "In Progress": { label: "En progreso", variant: "warning" },
  created: { label: "Pendiente", variant: "info" },
};

function getStatusInfo(status: ReportStatus) {
  return STATUS_MAP[status] ?? { label: status, variant: "neutral" as const };
}


// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface HighlightsPanelProps {
  folio: string | null;
  status: ReportStatus;
  tenantName?: string;
  propertyAlias?: string;
  rentalPrice?: number;
  createdAt?: string;
  onShareClick: () => void;
  className?: string;
}

function HighlightsPanel({
  folio,
  status,
  tenantName,
  propertyAlias,
  rentalPrice,
  createdAt,
  onShareClick,
  className,
}: HighlightsPanelProps) {
  const isMobile = useIsMobile();
  const statusInfo = getStatusInfo(status);

  const metrics = [
    { icon: UserIcon, label: "Inquilino", value: tenantName },
    { icon: HomeIcon, label: "Inmueble", value: propertyAlias },
    {
      icon: CalendarIcon,
      label: "Renta mensual",
      value: rentalPrice != null ? formatCurrency(rentalPrice) : undefined,
    },
  ].filter((m) => m.value);

  return (
    <Card className={cn("gap-0", className)}>
      <CardContent className="space-y-5">
        {/* Title row */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold leading-tight">
              Solicitud de Renta:{" "}
              {folio ? `Folio #${folio}` : "Sin folio"}
            </h2>
            <div className="flex items-center gap-2">
              <StatusBadge status={statusInfo.variant} label={statusInfo.label} />
              {createdAt && (
                <span className="text-xs text-muted-foreground">{createdAt}</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onShareClick}>
              <ShareIcon className="size-4" />
              {!isMobile && "Compartir"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="size-8">
                  <MoreHorizontalIcon className="size-4" />
                  <span className="sr-only">Más acciones</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onShareClick}>
                  <ShareIcon className="size-4" />
                  Compartir enlace
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Key metrics */}
        {metrics.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {metrics.map((m) => (
              <div key={m.label} className="flex items-center gap-1.5 text-sm">
                <m.icon className="size-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">{m.label}:</span>
                <span className="font-medium sentry-mask">{m.value}</span>
              </div>
            ))}
          </div>
        )}

      </CardContent>
    </Card>
  );
}

export { HighlightsPanel, getStatusInfo };
export type { HighlightsPanelProps };
